/** Thin wrapper over fetch. Same origin in production, proxied by Vite in dev,
 *  so there is never a base URL or a CORS preflight to think about. */
import { beginActivity } from './activity.js';
import { offlineRequest } from './engine/offlineSites.js';

function requestLabel(method, url) {
  const reading = method === 'GET', path = url.split('?')[0];
  if (path.includes('/light-libraries')) return reading ? 'Loading light library…' : 'Updating light library…';
  if (path.includes('/system/catalogues/lights/import/')) return path.endsWith('/preview') ? 'Checking lighting import…' : 'Importing lights…';
  if (path.includes('/auth/')) return path.endsWith('/logout') ? 'Signing out…' : path.endsWith('/password') ? 'Updating password…' : 'Connecting to your account…';
  if (path.includes('/images')) return reading ? 'Loading images…' : method === 'DELETE' ? 'Removing image…' : 'Uploading image…';
  if (path.includes('/import/')) return path.endsWith('/preview') ? 'Checking camera import…' : 'Importing cameras…';
  if (path.includes('/models') || path.includes('/catalogues')) return reading || path.endsWith('/resolve') ? 'Loading camera library…' : 'Updating camera library…';
  if (path.includes('/shares') || path.includes('/share/')) return reading ? 'Loading shared plans…' : 'Updating share links…';
  if (path.includes('/restore/')) return 'Restoring saved version…';
  if (path === '/api/projects/recovery-archive') return 'Restoring backup ZIP…';
  if (path.includes('/recovery-photos/')) return 'Restoring backup photos…';
  if (path.includes('/versions')) return reading ? 'Loading version history…' : 'Saving plan…';
  if (path === '/api/projects') return reading ? 'Loading sites…' : 'Creating site…';
  return reading ? 'Opening plan…' : method === 'DELETE' ? 'Deleting site…' : 'Updating site…';
}

export class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function call(method, url, body, opts = {}) {
  const activity = beginActivity(requestLabel(method, url));
  try { return await offlineRequest(method, url, body, opts, request); } finally { activity.finish(); }
}

async function request(method, url, body, opts) {
  const headers = { ...(opts.headers || {}) };
  let payload;
  if (body instanceof Blob || body instanceof ArrayBuffer || ArrayBuffer.isView(body)) {
    payload = body;
  } else if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    payload = JSON.stringify(body);
  }

  let res;
  try {
    const timeout = AbortSignal.timeout(opts.timeoutMs || 20000);
    const signal = opts.signal ? (AbortSignal.any ? AbortSignal.any([opts.signal, timeout]) : opts.signal) : timeout;
    res = await fetch(url, { method, headers, body: payload, credentials: 'same-origin', signal });
  } catch {
    if (opts.signal?.aborted) throw new DOMException('Request cancelled.', 'AbortError');
    throw new ApiError('Could not reach the server. Check your connection and try again.', 0);
  }

  if (res.status === 204) return null;
  const type = res.headers.get('content-type') || '';
  if (!type.includes('json')) {
    if (!res.ok) throw new ApiError(`The server returned ${res.status}.`, res.status);
    return res;
  }
  const data = await res.json();
  if (!res.ok) throw new ApiError(data?.error || `The server returned ${res.status}.`, res.status);
  return data;
}

const get = (u) => call('GET', u);
const post = (u, b, o) => call('POST', u, b, o);
const patch = (u, b) => call('PATCH', u, b);
const put = (u, b, o) => call('PUT', u, b, o);
const del = (u) => call('DELETE', u);

export const api = {
  ApiError,
  samples: { list: () => get('/api/samples'), read: id => get('/api/samples/' + encodeURIComponent(id)) },

  lightLibraries: {
    list: filters => get('/api/light-libraries?' + new URLSearchParams(filters)),
    preview: body => post('/api/light-libraries/preview', body),
    apply: body => post('/api/light-libraries/apply', body),
    remove: (id, revision) => del(`/api/light-libraries/${encodeURIComponent(id)}?revision=${revision}`)
  },

  auth: {
	setupStatus: () => get('/api/setup/status'),
	setup: (email, name, password) => post('/api/setup', { email, name, password }),
    me: () => get('/api/auth/me'),
    login: (email, password) => post('/api/auth/login', { email, password }),
    logout: () => post('/api/auth/logout'),
    changePassword: (current, next) => post('/api/auth/password', { current, next })
  },

  tenant: {
	read: () => get('/api/tenant'),
	update: company => patch('/api/tenant', company),
	users: () => get('/api/tenant/users'),
	createUser: user => post('/api/tenant/users', user),
	updateUser: (id, changes) => patch(`/api/tenant/users/${encodeURIComponent(id)}`, changes),
	resetPassword: (id, password) => post(`/api/tenant/users/${encodeURIComponent(id)}/password`, { password })
  },

  system: {
	overview: () => get('/api/system/overview'),
	tenants: () => get('/api/system/tenants'),
	createTenant: body => post('/api/system/tenants', body),
	updateTenant: (id, company) => patch(`/api/system/tenants/${encodeURIComponent(id)}`, company),
	resetOwnerPassword: (id, password) => post(`/api/system/tenants/${encodeURIComponent(id)}/owner/password`, { password }),
	reloadCatalogues: () => post('/api/system/catalogues/reload'),
	cameras: filters => get('/api/system/catalogues/cameras?' + new URLSearchParams(filters || {})),
	createCamera: model => post('/api/system/catalogues/cameras', { model }),
	updateCamera: (id, model, revision) => patch(`/api/system/catalogues/cameras/${encodeURIComponent(id)}`, { model, revision }),
	removeCamera: (id, revision) => del(`/api/system/catalogues/cameras/${encodeURIComponent(id)}?revision=${revision}`),
	previewCameraImport: body => post('/api/system/catalogues/cameras/import/preview', body),
	applyCameraImport: body => post('/api/system/catalogues/cameras/import/apply', body),
	lights: filters => get('/api/system/catalogues/lights?' + new URLSearchParams(filters || {})),
	createLight: definition => post('/api/system/catalogues/lights', { definition }),
	updateLight: (id, definition, revision) => patch(`/api/system/catalogues/lights/${encodeURIComponent(id)}`, { definition, revision }),
	removeLight: (id, revision) => del(`/api/system/catalogues/lights/${encodeURIComponent(id)}?revision=${revision}`),
	previewLightImport: body => post('/api/system/catalogues/lights/import/preview', body),
	applyLightImport: body => post('/api/system/catalogues/lights/import/apply', body)
  },

  projects: {
    list: (archived = false) => get(`/api/projects${archived ? '?archived=1' : ''}`),
    create: (name, doc, opts) => post('/api/projects', { name, doc }, opts),
    restoreArchive: (file, signal) => post('/api/projects/recovery-archive', file, { headers: { 'Content-Type': 'application/zip' }, signal, timeoutMs: 300000 }),
    restoreArchivePhoto: (id, slot, bytes, mime, signal) => put(`/api/projects/${encodeURIComponent(id)}/recovery-photos/${slot}`, new Blob([bytes], { type: mime }), { headers: { 'Content-Type': mime }, signal, timeoutMs: 120000 }),
    read: id => get(`/api/projects/${id}`),
    update: (id, patchBody) => patch(`/api/projects/${id}`, patchBody),
    remove: id => del(`/api/projects/${id}`),
    save: (id, doc, note, expectedRev, offlineVersion) => post(`/api/projects/${id}/versions`, { doc, note, expectedRev }, { offlineVersion }),
    // Blob and guards are local transport options; only the ordinary document
    // save body may reach the server after the durable asset dependencies drain.
    // Internal opt-in; no Editor caller until lifecycle/recovery UI is ready.
    saveWithHistory: (id, doc, note, expectedRev, offlineVersion, state, historyAnchor, assertCurrent) => post(`/api/projects/${id}/versions`, { doc, note, expectedRev }, { offlineVersion, historyState: state, historyAnchor, localHistoryCommitOnly: true, assertCurrent }),
    saveReferenceWithHistory: (id, doc, note, expectedRev, offlineVersion, attempt, sourceState, state, historyAnchor, assertCurrent) => post(`/api/projects/${id}/versions`, { doc, note, expectedRev }, { offlineVersion, referenceUpload: attempt, sourceState, historyState: state, historyAnchor, localHistoryCommitOnly: true, assertCurrent }),
    saveReference: (id, doc, note, expectedRev, offlineVersion, attempt, assertCurrent) => post(`/api/projects/${id}/versions`, { doc, note, expectedRev }, { offlineVersion, referenceUpload: attempt, localCommitOnly: true, assertCurrent }),
    revision: id => get(`/api/projects/${id}/revision`),
    history: id => get(`/api/projects/${id}/versions`),
    version: (id, rev) => get(`/api/projects/${id}/versions/${rev}`),
    restore: (id, rev, expectedRev) => post(`/api/projects/${id}/restore/${rev}`, { expectedRev })
  },

  models: {
    list: (filters = {}) => get('/api/models?' + new URLSearchParams(filters)),
    resolve: ids => post('/api/models/resolve', { ids }),
    read: id => get(`/api/models/${encodeURIComponent(id)}`),
    previewImport: payload => post('/api/models/import/preview', payload),
    applyImport: payload => post('/api/models/import/apply', payload),
    exportUrl: (filters, format) => '/api/models/export?' + new URLSearchParams({ ...filters, format }),
    create: m => post('/api/models', m),
    update: (id, m) => patch(`/api/models/${id}`, m),
    duplicate: id => post(`/api/models/${id}/duplicate`),
    remove: id => del(`/api/models/${id}`)
  },

  catalogues: {
    counts: projectId => get('/api/catalogues?' + new URLSearchParams(projectId ? { projectId } : {})),
    list: filters => get('/api/catalogues/models?' + new URLSearchParams(filters)),
    select: (source, ids, filters) => post('/api/catalogues/select', { source, ids, filters }),
    preview: body => post('/api/catalogues/import/preview', body),
    apply: body => post('/api/catalogues/import/apply', body),
    create: (target, model) => post('/api/catalogues/models', { target, model }),
    update: (id, model, revision) => patch(`/api/catalogues/models/${id}`, { model, revision }),
    remove: (id, revision) => del(`/api/catalogues/models/${id}?revision=${revision}`),
    updates: target => post('/api/catalogues/updates', { target }),
    history: id => get(`/api/catalogues/models/${id}/history`),
    exportUrl: (filters, format) => '/api/catalogues/export?' + new URLSearchParams({ ...filters, format })
  },

  images: {
    uploadCapabilities: () => get('/api/image-uploads/capabilities'),
    uploadReviewed: attempt => call('PUT', `/api/projects/${encodeURIComponent(attempt.projectID)}/images/${attempt.uploadID}`, attempt.bytes, { headers: attempt.headers }),
    list: projectId => get(`/api/projects/${projectId}/images`),
    upload: (projectId, file) =>
      post(`/api/projects/${projectId}/images`, file, {
        headers: { 'Content-Type': file.type, 'X-Filename': file.name || 'floorplan' }
      }),
    remove: id => del(`/api/images/${id}`),
    url: (id, token) => `/api/images/${id}${token ? `?t=${encodeURIComponent(token)}` : ''}`
  },

  shares: {
    list: projectId => get(`/api/projects/${projectId}/shares`),
    create: (projectId, body) => post(`/api/projects/${projectId}/shares`, body),
    revoke: token => del(`/api/shares/${token}`),
    read: token => get(`/api/share/${encodeURIComponent(token)}`)
  }
};
